
import { useEffect, useRef, useState } from 'react';

const TYPEWRITER_WORDS = ['PRECISIÓN OMR', 'EXACTITUD TOTAL', 'TECNOLOGÍA OMR'];

// Respuestas predefinidas para la ficha óptica (A=0, B=1, C=2, D=3, E=4)
const MARKED_ANSWERS = [2, 0, 3, 1, 4, 2, 0, 3, 1, 4, 2, 3, 0, 4, 1];

function useCountUp(target: number, duration: number, start: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}

function OMRRows() {
  const letters = ['A', 'B', 'C', 'D', 'E'];
  return (
    <div className="space-y-1.5">
      {MARKED_ANSWERS.map((markedIdx, rowIdx) => (
        <div
          key={rowIdx}
          className="flex items-center px-1 py-0.5 rounded-lg"
          style={{
            background: rowIdx % 2 === 0 ? 'rgba(13,148,136,0.04)' : 'transparent',
          }}
        >
          {/* Número de pregunta */}
          <span
            className="text-[10px] font-bold w-8 text-center flex-shrink-0"
            style={{ color: '#6B7280' }}
          >
            {rowIdx + 1}
          </span>
          {/* Burbujas A B C D E */}
          {letters.map((letter, colIdx) => {
            const isMarked = colIdx === markedIdx;
            return (
              <div key={letter} className="flex-1 flex items-center justify-center">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black transition-all"
                  style={
                    isMarked
                      ? {
                          background: 'linear-gradient(135deg, #0D9488, #0f766e)',
                          color: '#ffffff',
                          boxShadow: '0 0 8px rgba(13,148,136,0.6)',
                          animation: `mark-bubble 0.4s ${rowIdx * 0.08}s ease both`,
                          border: '2px solid #0D9488',
                        }
                      : {
                          background: 'transparent',
                          color: '#9CA3AF',
                          border: '1.5px solid #D1D5DB',
                        }
                  }
                >
                  {letter}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default function Hero() {
  const [visible, setVisible] = useState(false);
  const [wordIndex, setWordIndex] = useState(0);
  const [displayed, setDisplayed] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  const precision = useCountUp(99, 1800, statsVisible);
  const exams = useCountUp(500000, 2200, statsVisible);
  const clients = useCountUp(120, 1600, statsVisible);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const word = TYPEWRITER_WORDS[wordIndex];
    let timeout: ReturnType<typeof setTimeout>;
    if (!isDeleting && displayed.length < word.length) {
      timeout = setTimeout(() => setDisplayed(word.slice(0, displayed.length + 1)), 80);
    } else if (!isDeleting && displayed.length === word.length) {
      timeout = setTimeout(() => setIsDeleting(true), 2000);
    } else if (isDeleting && displayed.length > 0) {
      timeout = setTimeout(() => setDisplayed(displayed.slice(0, -1)), 45);
    } else if (isDeleting && displayed.length === 0) {
      setIsDeleting(false);
      setWordIndex((prev) => (prev + 1) % TYPEWRITER_WORDS.length);
    }
    return () => clearTimeout(timeout);
  }, [displayed, isDeleting, wordIndex]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('contacto');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="inicio"
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0f2027 0%, #1a3a3a 40%, #0D9488 100%)' }}
    >
      {/* Fondo animado */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, #F59E0B 0%, transparent 70%)', animation: 'pulse-slow 6s ease-in-out infinite' }}
        />
        <div
          className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full opacity-15"
          style={{ background: 'radial-gradient(circle, #0D9488 0%, transparent 70%)', animation: 'pulse-slow 8s ease-in-out infinite reverse' }}
        />
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Partículas flotantes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              width: `${4 + (i % 4) * 3}px`,
              height: `${4 + (i % 4) * 3}px`,
              background: i % 2 === 0 ? '#F59E0B' : '#5EEAD4',
              top: `${10 + (i * 7) % 80}%`,
              left: `${5 + (i * 9) % 90}%`,
              opacity: 0.25 + (i % 3) * 0.1,
              animation: `float-particle ${4 + (i % 4)}s ease-in-out infinite`,
              animationDelay: `${i * 0.6}s`,
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes pulse-slow {
          0%, 100% { transform: scale(1); opacity: 0.15; }
          50% { transform: scale(1.15); opacity: 0.25; }
        }
        @keyframes float-particle {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-18px) rotate(120deg); }
          66% { transform: translateY(10px) rotate(240deg); }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in-left {
          from { opacity: 0; transform: translateX(-40px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fade-in-right {
          from { opacity: 0; transform: translateX(40px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes glow-pulse {
          0%, 100% { box-shadow: 0 0 20px rgba(245,158,11,0.4); }
          50% { box-shadow: 0 0 40px rgba(245,158,11,0.8), 0 0 60px rgba(245,158,11,0.3); }
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes scan-line {
          0% { top: 2%; opacity: 0.9; }
          90% { opacity: 0.7; }
          100% { top: 98%; opacity: 0; }
        }
        @keyframes mark-bubble {
          0% { transform: scale(0); opacity: 0; }
          60% { transform: scale(1.25); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes float-card {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes rotate-ring {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes glow-border {
          0%, 100% { box-shadow: 0 0 20px rgba(13,148,136,0.35), 0 0 40px rgba(13,148,136,0.15); }
          50% { box-shadow: 0 0 35px rgba(13,148,136,0.65), 0 0 70px rgba(13,148,136,0.35); }
        }
        .animate-glow { animation: glow-pulse 2.5s ease-in-out infinite; }
        .shimmer-text {
          background: linear-gradient(90deg, #F59E0B, #FCD34D, #F59E0B, #FCD34D);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 3s linear infinite;
        }
        .cursor-blink::after {
          content: '|';
          animation: blink 0.8s step-end infinite;
          color: #F59E0B;
          font-weight: 300;
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}</style>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-36 lg:pt-44 pb-20 sm:pb-28 lg:pb-32">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">

          {/* ── Columna izquierda ── */}
          <div
            className="lg:col-span-3 space-y-7"
            style={{ opacity: visible ? 1 : 0, animation: visible ? 'fade-in-left 0.9s ease forwards' : 'none' }}
          >
            {/* Badge */}
            <div
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#F59E0B]/50 animate-glow"
              style={{ background: 'rgba(245,158,11,0.12)' }}
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#F59E0B] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#F59E0B]"></span>
              </span>
              <span className="text-[#FCD34D] font-bold text-sm tracking-widest uppercase">Tecnología de Precisión</span>
            </div>

            {/* Título */}
            <div className="space-y-2">
              <div
                className="text-white font-extrabold leading-none tracking-tight"
                style={{ fontSize: 'clamp(2.8rem, 6vw, 5.5rem)' }}
              >
                <span className="block" style={{ opacity: visible ? 1 : 0, animation: visible ? 'slide-up 0.7s 0.1s ease forwards' : 'none' }}>
                  EVALUAMOS TUS
                </span>
                <span className="block" style={{ opacity: visible ? 1 : 0, animation: visible ? 'slide-up 0.7s 0.25s ease forwards' : 'none' }}>
                  RESULTADOS CON
                </span>
                <span
                  className="block cursor-blink shimmer-text"
                  style={{ opacity: visible ? 1 : 0, animation: visible ? 'slide-up 0.7s 0.4s ease forwards' : 'none', minHeight: '1.2em' }}
                >
                  {displayed}
                </span>
              </div>
            </div>

            {/* Descripción */}
            <p
              className="text-lg sm:text-xl text-white/70 max-w-xl leading-relaxed"
              style={{ opacity: visible ? 1 : 0, animation: visible ? 'slide-up 0.7s 0.55s ease forwards' : 'none' }}
            >
              Reconocimiento óptico de marcas de alta precisión para instituciones educativas que exigen rapidez, confiabilidad y resultados impecables.
            </p>

            {/* Botones */}
            <div
              className="flex flex-col sm:flex-row gap-4"
              style={{ opacity: visible ? 1 : 0, animation: visible ? 'slide-up 0.7s 0.7s ease forwards' : 'none' }}
            >
              <button
                onClick={scrollToContact}
                className="group relative overflow-hidden bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-white px-8 py-4 rounded-full font-bold text-base hover:shadow-2xl transition-all hover:scale-105 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                style={{ boxShadow: '0 8px 30px rgba(245,158,11,0.4)' }}
              >
                <span className="relative z-10 flex items-center gap-2">
                  Solicitar Cotización
                  <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform"></i>
                </span>
                <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500 skew-x-12"></div>
              </button>
              <button
                onClick={scrollToContact}
                className="group border-2 border-white/40 text-white px-8 py-4 rounded-full font-bold text-base hover:bg-white/10 hover:border-white/70 transition-all hover:scale-105 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer backdrop-blur-sm"
              >
                Contáctanos
                <i className="ri-message-3-line text-lg group-hover:rotate-12 transition-transform"></i>
              </button>
            </div>

            {/* Estadísticas */}
            <div
              ref={statsRef}
              className="grid grid-cols-3 gap-4 pt-4"
              style={{ opacity: visible ? 1 : 0, animation: visible ? 'slide-up 0.7s 0.85s ease forwards' : 'none' }}
            >
              {[
                { value: precision, suffix: '%', label: 'Precisión', icon: 'ri-checkbox-circle-fill', color: '#0D9488' },
                { value: exams >= 500000 ? '500K+' : `${Math.floor(exams / 1000)}K`, suffix: '', label: 'Exámenes', icon: 'ri-file-list-3-line', color: '#F59E0B', raw: true },
                { value: clients, suffix: '+', label: 'Clientes', icon: 'ri-building-2-line', color: '#5EEAD4' },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="rounded-2xl p-4 text-center border border-white/10 backdrop-blur-sm"
                  style={{ background: 'rgba(255,255,255,0.07)' }}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full mx-auto mb-2" style={{ background: `${stat.color}25` }}>
                    <i className={`${stat.icon} text-xl`} style={{ color: stat.color }}></i>
                  </div>
                  <p className="text-2xl sm:text-3xl font-extrabold text-white">
                    {stat.raw ? stat.value : `${stat.value}${stat.suffix}`}
                  </p>
                  <p className="text-xs text-white/50 font-medium mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Columna derecha — Scanner OMR ── */}
          <div
            className="lg:col-span-2 flex items-center justify-center"
            style={{ opacity: visible ? 1 : 0, animation: visible ? 'fade-in-right 1s 0.3s ease forwards' : 'none' }}
          >
            {/* Contenedor con padding para que las tarjetas flotantes no se corten */}
            <div className="relative pt-14 pb-14 px-10" style={{ animation: 'float-card 5s ease-in-out infinite' }}>

              {/* Anillo giratorio */}
              <div
                className="absolute pointer-events-none"
                style={{
                  inset: '40px 20px',
                  borderRadius: '32px',
                  background: 'conic-gradient(from 0deg, #F59E0B55, #0D948855, #F59E0B55)',
                  animation: 'rotate-ring 8s linear infinite',
                }}
              />

              {/* Glow difuso */}
              <div
                className="absolute pointer-events-none"
                style={{
                  inset: '44px 24px',
                  borderRadius: '28px',
                  background: 'linear-gradient(135deg, rgba(245,158,11,0.3), rgba(13,148,136,0.3))',
                  filter: 'blur(20px)',
                }}
              />

              {/* Imagen del scanner OMR */}
              <div
                className="relative rounded-3xl overflow-hidden"
                style={{
                  width: 'min(280px, 72vw)',
                  height: 'min(330px, 85vw)',
                  border: '2px solid rgba(13,148,136,0.45)',
                  animation: 'glow-border 3s ease-in-out infinite',
                  boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
                }}
              >
                <img
                  src="https://public.readdy.ai/ai/img_res/ccfcbd51a6673683b70811ebb376c43c.jpg"
                  alt="Scanner OMR procesando fichas ópticas"
                  className="w-full h-full object-cover object-center"
                />

                {/* Overlay degradado inferior */}
                <div
                  className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
                  style={{ background: 'linear-gradient(to top, rgba(13,148,136,0.55), transparent)' }}
                />

                {/* Línea de escaneo animada */}
                <div
                  className="absolute left-0 right-0 h-0.5 pointer-events-none z-20"
                  style={{
                    background: 'linear-gradient(90deg, transparent 5%, #0D9488 30%, #F59E0B 50%, #0D9488 70%, transparent 95%)',
                    animation: 'scan-line 2.8s linear infinite',
                    boxShadow: '0 0 12px rgba(13,148,136,0.9), 0 0 24px rgba(13,148,136,0.4)',
                  }}
                />

                {/* Badge inferior */}
                <div className="absolute bottom-4 left-0 right-0 flex justify-center z-10">
                  <div
                    className="flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-sm border border-white/30"
                    style={{ background: 'rgba(13,148,136,0.75)' }}
                  >
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#F59E0B] opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-[#F59E0B]"></span>
                    </span>
                    <span className="text-white text-xs font-bold tracking-widest uppercase">Escaneando en tiempo real</span>
                  </div>
                </div>
              </div>

              {/* Card flotante — Precisión (arriba a la derecha, dentro del padding) */}
              <div
                className="absolute top-2 right-0 bg-white rounded-2xl px-4 py-3 shadow-2xl border border-gray-100 z-30"
                style={{ animation: 'float-card 4s ease-in-out infinite' }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 bg-[#0D9488]/15 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-xl text-[#0D9488]"></i>
                  </div>
                  <div>
                    <p className="text-xl font-extrabold text-[#1A1A2E]">99%</p>
                    <p className="text-[10px] text-gray-500 font-semibold">Precisión</p>
                  </div>
                </div>
              </div>

              {/* Card flotante — Tiempo (abajo a la izquierda, dentro del padding) */}
              <div
                className="absolute bottom-2 left-0 bg-white rounded-2xl px-4 py-3 shadow-2xl border border-gray-100 z-30"
                style={{ animation: 'float-card 5s ease-in-out infinite', animationDelay: '1.5s' }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 bg-[#F59E0B]/15 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-xl text-[#F59E0B]"></i>
                  </div>
                  <div>
                    <p className="text-lg font-extrabold text-[#1A1A2E]">24 Hrs</p>
                    <p className="text-[10px] text-gray-500 font-semibold">Resultados</p>
                  </div>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>

      {/* Flecha scroll */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce">
        <span className="text-white/40 text-xs tracking-widest uppercase">Scroll</span>
        <i className="ri-arrow-down-line text-2xl text-white/40"></i>
      </div>
    </section>
  );
}
